import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Button,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  IconButton,
  Box,
  Badge,
} from "@chakra-ui/react";
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiSearch, 
  FiEdit 
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { get_defective_returns } from "../../redux/DefectiveSlice";
import CustomButton from "../../components/CustomButton";
import DisplayData from "../../components/DisplayData";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const DefectiveReturnList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { defective_returns, total_count, loading } = useSelector(
    (state) => state.defectiveReturns
  );

  useEffect(() => {
    if (location.state?.fromDetails) {
      const detailPage = location.state.page;
      const detailRows = location.state.rows;
      const detailInitialValue = location.state.initialValue;
      
      setPage(detailPage);
      setRows(detailRows);
      setInitialValue(detailInitialValue);
      setSearchQuery(""); 
      
      store.dispatch(get_defective_returns({ page: detailPage, limit: detailRows }));
      
      window.history.replaceState({}, document.title);
    } else {
      store.dispatch(get_defective_returns({ page, limit: rows }));
    }
  }, [location.state?.fromDetails]);

  const onPrevious = () => {
    if (page > 1) {
      const newPage = page - 1;
      setInitialValue(Number(initialValue) - Number(rows));
      setPage(newPage);
      setSearchQuery("");
      store.dispatch(get_defective_returns({ page: newPage, limit: rows }));
    }
  };

  const onNext = () => {
    if (rows * page < total_count) {
      const newPage = page + 1;
      setInitialValue(Number(initialValue) + Number(rows));
      setPage(newPage);
      setSearchQuery("");
      store.dispatch(get_defective_returns({ page: newPage, limit: rows }));
    }
  };

  const handleEdit = (item, e) => {
    e.stopPropagation();
    navigate("edit", {
      state: {
        returnId: item,
        returnData: item,
        isEdit: true,
        page,
        rows,
        initialValue,
      },
    });
  };

  // -----------------------------------
  // NEW: Badge mapping for Status Codes
  // -----------------------------------
  const getStatusBadge = (status) => {
    switch (status) {
      case "0":
        return { text: "New Received", color: "green" };
      case "1":
        return { text: "Assigned", color: "orange" };
      case "2":
        return { text: "DP Approved", color: "purple" };
      case "3":
        return { text: "Completed", color: "blue" };
      default:
        return null;
    }
  };

  const filteredReturns = searchQuery
    ? defective_returns.filter((item) =>
        item.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : defective_returns;

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl" flexShrink={0}>
          Defective Returns
        </Heading>

        <Spacer display={["none", "none", "block"]} />

        <Flex
          direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
        >
          <Select
            placeholder="Rows"
            size="md"
            w={["100%", "100px"]}
            value={rows}
            onChange={(e) => {
              const newRows = e.target.value;
              setRows(newRows);
              setPage(1);
              setInitialValue(1);
              setSearchQuery("");
              store.dispatch(get_defective_returns({ page: 1, limit: newRows }));
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>

          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "auto"]} maxW="sm">
            <Input
              placeholder="Search by Vendor Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>
        </Flex>
      </Card>

      {loading ? (
        <GasCylinderLoader 
          size="sm" 
          loadingText="Loading Defective returns"
        />
      ) : (
        <>
          {searchQuery && filteredReturns.length === 0 ? (
            <Card mt={4} p={8} textAlign="center">
              <Text fontSize="lg" color="gray.500">
                No results found for "{searchQuery}"
              </Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                Try adjusting your search term
              </Text>
            </Card>
          ) : (
            <SimpleGrid minChildWidth="xs" spacing="10px" mt={4}>
              {filteredReturns.map((item) => {
                  const statusValue = item.details?.[0]?.status ?? item.status;
                const badge = getStatusBadge(statusValue);

                return (
                  <Card
                    key={item.id}
                    paddingInline={5}
                    paddingBlock={2}
                    position="relative"
                    cursor="pointer"
                    _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
                    transition="all 0.2s ease"
                    onClick={() =>
                      navigate("detail", {
                        state: {
                          returnData: item,
                          page,
                          rows,
                          initialValue,
                        },
                      })
                    }
                  >
                    {/* Edit allowed only when status = 2 */}
      {/* Status Badge + Edit Icon in One Row */}
<Flex
  position="absolute"
  top="8px"
  right="10px"
  align="center"
  gap={2}
>
  {/* Badge */}
  {badge && (
    <Badge
      colorScheme={badge.color}
      fontSize="xs"
      px={2}
      py={1}
      borderRadius="md"
    >
      {badge.text}
    </Badge>
  )}

  {/* Edit icon only when status = 2 */}
  {statusValue === "2" && (
    <IconButton
      icon={<FiEdit />}
      size="sm"
      onClick={(e) => handleEdit(item, e)}
      aria-label="Edit defective return"
    />
  )}
</Flex>

                    <DisplayData
                      fs="16px"
                      fw="bold"
                      v_color="#007bc9"
                      value={item.customer_name || "NA"}
                    />

                    <Box mt={1}>
                      {(() => {
                        const hasReturnDate =
                          Array.isArray(item.details) &&
                          item.details.some((detail) =>
                            Array.isArray(detail.assign_details) &&
                            detail.assign_details.some(
                              (assign) => assign.return_date && assign.return_date !== "0000-00-00"
                            )
                          );  

                        const fields = [
                          ...(hasReturnDate
                            ? [
                                { title: "Invoice No", value: item.sr_no },
                                { title: "Def Return ID", value: item.def_return_id },
                              ]
                            : [{ title: "Def Return ID", value: item.def_return_id }]),
                          {
                            title: "Defective Return Date",
                            value: item.createdat
                              ? new Date(item.createdat).toLocaleString()
                              : "NA",
                          },
                          { title: "Ref. Order ID", value: item.orderid },
                          { title: "Ref. Invoice Date", value: item.sr_date },
                          { title: "Ref. Return Date", value: item.def_return_date },
                        ];

                        return fields.map((field, idx) => (
                          <Flex
                            key={idx}
                            justify="space-between"
                            align="center"
                            mb="1"
                            wrap="nowrap"
                          >
                            <Text fontSize="sm" color="gray.600" flex="1" noOfLines={1}>
                              {field.title}
                            </Text>
                            <Text
                              fontSize="sm"
                              color="gray.800"
                              flex="1"
                              textAlign="right"
                              noOfLines={1}
                            >
                              {field.value || "NA"}
                            </Text>
                          </Flex>
                        ));
                      })()}
                    </Box>
                  </Card>
                );
              })}
            </SimpleGrid>
          )}

          {!searchQuery && (
            <Flex justify="flex-end" mt={4}>
              <Card
                alignItems="center"
                flexDirection="row"
                gap={4}
                py={1}
                px={4}
                border="1px solid #E2E8F0"
                boxShadow="sm"
                borderRadius="md"
                bg="white"
              >
                <FiChevronLeft
                  cursor={page === 1 ? "not-allowed" : "pointer"}
                  color={page === 1 ? "gray" : "#007bc9"}
                  size={28}
                  onClick={onPrevious}
                />

                <Flex align="center" gap={2} fontSize="sm" fontWeight="medium">
                  <Text>{initialValue}</Text>
                  <Text>-</Text>
                  <Text>
                    {rows * page >= total_count ? total_count : rows * page}
                  </Text>
                  <Text>of</Text>
                  <Text>{total_count}</Text>
                </Flex>

                <FiChevronRight
                  cursor={rows * page >= total_count ? "not-allowed" : "pointer"}
                  color={rows * page >= total_count ? "gray" : "#007bc9"}
                  size={28}
                  onClick={onNext}
                />
              </Card>
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default DefectiveReturnList;
